import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, Globe, Lock, ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { useProjectAssetsStore } from '../stores/projectAssetsStore'
import { useAuthStore } from '../stores/authStore'
import { ProjectAsset } from '../services/projectAssetsApi'

interface ProjectAssetsOverviewProps {
  projectId: string
  projectName: string
  projectTenantId: string
}


const ProjectAssetsOverview: React.FC<ProjectAssetsOverviewProps> = ({ projectId, projectName, projectTenantId }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAsset, setEditingAsset] = useState<ProjectAsset | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assetType: '',
    webUrl: '',
    loginUrl: '',
    credentialEmail: '',
    credentialPassword: ''
  })

  const steps = [
    { id: 1, name: 'Basic Info', description: 'Asset details' },
    { id: 2, name: 'URLs', description: 'Web and login URLs' },
    { id: 3, name: 'Credentials', description: 'Test credentials' },
    { id: 4, name: 'Preview', description: 'Review and confirm' }
  ]
  
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { 
    assets, 
    pagination, 
    loading, 
    error, 
    fetchProjectAssets, 
    createProjectAsset, 
    updateProjectAsset, 
    deleteProjectAsset, 
    clearError 
  } = useProjectAssetsStore()
  
  // Check if user has admin role (only ADMIN can manage assets)
  const canManageAssets = user?.roles?.includes('ADMIN')
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setCurrentPage(1) // Reset to first page when searching
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (projectId) {
      fetchProjectAssets(projectId, { page: currentPage, limit: 10, search: debouncedSearch })
    }
  }, [fetchProjectAssets, projectId, currentPage, debouncedSearch])


  const getAssetTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'WEB_APPLICATION': 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300',
      'MOBILE_APPLICATION': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'API': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      'DESKTOP_APPLICATION': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      'DATABASE': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    }
    return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Modal handlers
  const openCreateModal = () => {
    setEditingAsset(null)
    setFormData({
      name: '',
      description: '',
      assetType: '',
      webUrl: '',
      loginUrl: '',
      credentialEmail: '',
      credentialPassword: ''
    })
    setCurrentStep(1)
    setShowCreateModal(true)
  }

  const openEditModal = (asset: ProjectAsset) => {
    setEditingAsset(asset)
    setFormData({
      name: asset.name,
      description: asset.description,
      assetType: asset.assetType,
      webUrl: asset.webUrl || '',
      loginUrl: asset.loginUrl || '',
      credentialEmail: asset.credentialEmail || '',
      credentialPassword: asset.credentialPassword || ''
    })
    setCurrentStep(1)
    setShowCreateModal(true)
  }

  const closeModals = () => {
    setShowCreateModal(false)
    setEditingAsset(null)
    setCurrentStep(1)
    setFormData({
      name: '',
      description: '',
      assetType: '',
      webUrl: '',
      loginUrl: '',
      credentialEmail: '',
      credentialPassword: ''
    })
  }

  const nextStep = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return !!(formData.name?.trim() && formData.assetType && formData.description?.trim())
      case 2:
        return !!(formData.webUrl?.trim() && formData.loginUrl?.trim()) // webUrl and loginUrl are mandatory
      case 3:
        return !!(formData.credentialEmail?.trim() && formData.credentialPassword?.trim()) // Credentials are mandatory
      case 4:
        // Final step - validate all required fields
        return !!(
          formData.name?.trim() && 
          formData.assetType && 
          formData.description?.trim() &&
          formData.webUrl?.trim() &&
          formData.loginUrl?.trim() &&
          formData.credentialEmail?.trim() &&
          formData.credentialPassword?.trim()
        )
      default:
        return false
    }
  }

  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate mandatory fields
    if (!formData.webUrl || !formData.webUrl.trim()) {
      alert('Web URL is required')
      return
    }
    if (!formData.loginUrl || !formData.loginUrl.trim()) {
      alert('Login URL is required')
      return
    }
    if (!formData.credentialEmail || !formData.credentialEmail.trim()) {
      alert('Test Email is required')
      return
    }
    if (!formData.credentialPassword || !formData.credentialPassword.trim()) {
      alert('Test Password is required')
      return
    }
    
    // Use the project's tenant ID
    if (!projectTenantId) {
      console.error('Project tenant ID is not available.')
      alert('Cannot create asset: Project tenant information is missing.')
      return
    }
    
    try {
      await createProjectAsset({ ...formData, projectId, tenantId: projectTenantId, assetType: formData.assetType as any })
      closeModals()
    } catch (err) {
      console.error('Failed to create project asset:', err)
    }
  }

  const handleUpdateAsset = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingAsset) return
    
    // Validate mandatory fields
    if (!formData.webUrl || !formData.webUrl.trim()) {
      alert('Web URL is required')
      return
    }
    if (!formData.loginUrl || !formData.loginUrl.trim()) {
      alert('Login URL is required')
      return
    }
    if (!formData.credentialEmail || !formData.credentialEmail.trim()) {
      alert('Test Email is required')
      return
    }
    if (!formData.credentialPassword || !formData.credentialPassword.trim()) {
      alert('Test Password is required')
      return
    }
    
    try {
      await updateProjectAsset(editingAsset._id, formData)
      closeModals()
    } catch (err) {
      console.error('Failed to update project asset:', err)
    }
  }


  if (loading && assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Project Assets</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage assets for {projectName}</p>
        </div>
        {canManageAssets && (
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={clearError}
                  className="text-sm bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-3 py-1 rounded-md hover:bg-red-200 dark:hover:bg-red-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Assets Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Assets</h3>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {pagination.total} asset{pagination.total !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>

        {assets.length === 0 ? (
          <div className="p-6">
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No assets found</h3>
                <p className="text-gray-500 dark:text-gray-400">Add your first asset to get started.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    URLs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Scans
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {assets.map((asset) => (
                  <tr 
                    key={asset._id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => navigate(`/projects/${projectId}/assets/${asset._id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {asset.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {asset.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAssetTypeColor(asset.assetType)}`}>
                        {asset?.assetType?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {asset.webUrl && (
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <Globe className="h-4 w-4 mr-1" />
                            <a 
                              href={asset.webUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="hover:text-primary-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {asset.webUrl}
                            </a>
                          </div>
                        )}
                        {asset.loginUrl && (
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <Lock className="h-4 w-4 mr-1" />
                            <a 
                              href={asset.loginUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="hover:text-primary-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {asset.loginUrl}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900 dark:text-white">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                          {asset.scanCount || 0} scan{(asset.scanCount || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(asset.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {canManageAssets && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                openEditModal(asset)
                              }}
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (window.confirm('Are you sure you want to delete this project asset?')) {
                                  deleteProjectAsset(asset._id)
                                }
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} assets
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-500"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={pagination.page * pagination.limit >= pagination.total}
                  className="px-3 py-2 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-500"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAsset) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-6 border w-full max-w-5xl max-h-[90vh] shadow-2xl rounded-lg bg-white dark:bg-gray-800 overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                {editingAsset ? 'Edit Project Asset' : 'Add New Project Asset'}
              </h3>

              {/* Stepper */}
              <div className="mb-6">
                <div className="flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    {steps.map((step, stepIdx) => (
                      <div key={step.id} className="flex items-center">
                        {/* Step Circle */}
                        <div className="relative">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${
                            currentStep > step.id
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : currentStep === step.id
                                ? 'bg-primary-600 border-primary-600 text-white shadow-lg'
                                : 'bg-white border-gray-300 text-gray-400 dark:bg-gray-700 dark:border-gray-600'
                          }`}>
                            {currentStep > step.id ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <span className="text-xs font-semibold">{step.id}</span>
                            )}
                          </div>
                          {/* Pulse effect for current step */}
                          {currentStep === step.id && (
                            <div className="absolute inset-0 rounded-full bg-primary-600 animate-ping opacity-20"></div>
                          )}
                        </div>

                        {/* Step Info */}
                        <div className="ml-2 min-w-0">
                          <div className={`text-xs font-semibold transition-colors ${
                            currentStep >= step.id
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {step.name}
                          </div>
                        </div>

                        {/* Connector Line */}
                        {stepIdx < steps.length - 1 && (
                          <div className={`ml-4 w-12 h-0.5 transition-colors ${
                            currentStep > step.id
                              ? 'bg-primary-600'
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault()
                if (currentStep === steps.length) {
                  if (editingAsset) {
                    handleUpdateAsset(e)
                  } else {
                    handleCreateAsset(e)
                  }
                }
              }}>
                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Asset Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Enter asset name"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Asset Type *
                        </label>
                        <select
                          value={formData.assetType}
                          onChange={(e) => setFormData({ ...formData, assetType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                          required
                        >
                          <option value="">Select asset type</option>
                          <option value="WEB_APPLICATION">Web Application</option>
                          <option value="MOBILE_APPLICATION">Mobile Application</option>
                          <option value="API">API</option>
                          <option value="DESKTOP_APPLICATION">Desktop Application</option>
                          <option value="DATABASE">Database</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Description *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        rows={4}
                        placeholder="Enter detailed asset description..."
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: URLs */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Note:</strong> Both Web URL and Login URL are required for all assets.
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Web URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.webUrl}
                        onChange={(e) => setFormData({ ...formData, webUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="https://example.com"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Required: Main website or application URL</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Login URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.loginUrl}
                        onChange={(e) => setFormData({ ...formData, loginUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="https://example.com/login"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Required: URL for the login page</p>
                    </div>
                  </div>
                )}

                {/* Step 3: Credentials */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Note:</strong> Test credentials are required for security testing purposes.
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Test Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.credentialEmail}
                        onChange={(e) => setFormData({ ...formData, credentialEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="test@example.com"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Required: Email for test account credentials</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Test Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={formData.credentialPassword}
                        onChange={(e) => setFormData({ ...formData, credentialPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Enter test password"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Required: Password for test account credentials</p>
                    </div>
                  </div>
                )}

                {/* Step 4: Preview */}
                {currentStep === 4 && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl p-6 border border-primary-200 dark:border-primary-700">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-800 rounded-lg flex items-center justify-center mr-3">
                          <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Asset Preview</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Review your asset details before creating</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Asset Name</label>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{formData.name || 'Not specified'}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Type</label>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAssetTypeColor(formData.assetType)}`}>
                              {formData.assetType?.replace('_', ' ') || 'Not specified'}
                            </span>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Web URL</label>
                            <p className="text-sm text-gray-900 dark:text-white break-all">{formData.webUrl || 'Not specified'}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Login URL</label>
                            <p className="text-sm text-gray-900 dark:text-white break-all">{formData.loginUrl || 'Not specified'}</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Description</label>
                            <p className="text-sm text-gray-900 dark:text-white line-clamp-3">{formData.description || 'Not specified'}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Test Email</label>
                            <p className="text-sm text-gray-900 dark:text-white truncate">{formData.credentialEmail || 'Not specified'}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Test Password</label>
                            <p className="text-sm text-gray-900 dark:text-white">{formData.credentialPassword ? '••••••••' : 'Not specified'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={(e) => prevStep(e)}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous
                      </button>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={closeModals}
                      className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>

                    {currentStep < steps.length ? (
                      <button
                        type="button"
                        onClick={(e) => nextStep(e)}
                        disabled={!isStepValid(currentStep)}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={!isStepValid(4)}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        {editingAsset ? 'Update Asset' : 'Create Asset'}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectAssetsOverview
